/**
 * DepartmentPicker — the department FK field for People, with inline create.
 *
 * People departments are `user_groups` rows of type `department` — the records
 * `/admin/groups?type=department` manages. Before WI 39f6ce30 this was a plain
 * `Select` offering only "None", and nothing in the People module could create a
 * department: an HR admin setting up a fresh tenant had no reachable way to fill
 * the headcount-by-department dashboard widget. Creating one is now part of
 * assigning one, which is how BambooHR, HiBob and Rippling all do it.
 *
 * Creation is eager rather than deferred (the JobTitleCombobox's "type to add"
 * defers because the backend promotes free text on save). `department_id` is a
 * real FK on the assignment, so the row has to exist before the form submits.
 *
 * `POST /groups` is `require_admin`, so the create affordance is gated on the
 * tenant-admin flag rather than letting a member find out via a 403.
 */
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Building2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { BidiText, EntityPickerBase } from '../../../shared/components/common';
import { useAuth } from '../../../shared/auth/AuthContext';
import { useApiError } from '../../../shared/hooks/useApiError';
import { useDepartments, useCreateDepartment } from '../hooks/useGroups';

interface Department {
  id: string;
  name: string;
}

interface DepartmentPickerProps {
  /** Selected department id, or '' when none. */
  value: string;
  onChange: (departmentId: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Hide the inline create action (filter contexts, where creating makes no sense). */
  hideAddNew?: boolean;
}

export function DepartmentPicker({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  hideAddNew = false,
}: DepartmentPickerProps) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { handleMutationError } = useApiError();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: departmentsData } = useDepartments();
  const { createDepartment, isPending } = useCreateDepartment();

  const canCreate = Boolean(user?.is_admin) && !hideAddNew;

  const departments = useMemo<Department[]>(() => {
    const all = (departmentsData?.data ?? []) as Department[];
    const query = search.trim().toLowerCase();
    if (!query) return all;
    return all.filter((d) => d.name.toLowerCase().includes(query));
  }, [departmentsData, search]);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const handleSelect = useCallback(
    (departmentId: string) => {
      onChange(departmentId);
      close();
    },
    [onChange, close],
  );

  const handleClear = useCallback(() => {
    onChange('');
    close();
  }, [onChange, close]);

  const handleAddNew = useCallback(async () => {
    const name = search.trim();
    if (!name || isPending) return;
    try {
      const created = await createDepartment(name);
      onChange(created.data.id);
      close();
    } catch (error) {
      // Keep the popover open on failure so the typed name is not lost.
      handleMutationError(error);
    }
  }, [search, isPending, createDepartment, onChange, close, handleMutationError]);

  // With nothing typed there is no name to create, so the row invites typing one
  // rather than opening a second dialog for the single field this entity has.
  const addNewLabel = search.trim()
    ? t('pickers.createDepartmentWithName', { name: search.trim() })
    : t('pickers.addNewDepartment');

  return (
    <EntityPickerBase<Department>
      value={value}
      items={departments}
      getItemLabel={(department) => department.name}
      renderItem={(department, isSelected) => (
        <>
          <Check className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
          <Building2 className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
          <BidiText className="truncate">{department.name}</BidiText>
        </>
      )}
      label={label}
      placeholder={placeholder ?? t('pickers.selectDepartment')}
      searchPlaceholder={t('pickers.searchDepartments')}
      emptyMessage={
        canCreate ? t('pickers.noDepartmentsFound') : t('pickers.noDepartmentsAdminOnly')
      }
      disabled={disabled}
      className={className}
      search={search}
      onSearchChange={setSearch}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
      onSelect={handleSelect}
      // "None" is a clear action, so it only belongs on a field that has
      // something to clear. Rendering it unconditionally also kept the list
      // non-empty forever, which suppressed the empty-state message a member on
      // a department-less tenant most needs to see.
      allowClear={Boolean(value)}
      onClear={handleClear}
      hideAddNew={!canCreate}
      addNewLabel={addNewLabel}
      onAddNew={handleAddNew}
    />
  );
}
